import { useEffect } from 'react'
import PregateHeader from '../components/PregateHeader'

/**
 * "Why I built this" page at `/why`. Must render fully with zero backend
 * dependency, same as LandingPage — see docs/decisions/2026-08-04-pow-returning-flag.md.
 * Shares the landing page's outer shell (padding, 960px max-width) so the header
 * and footer sit at identical positions on both pages; prose sections are
 * independently capped at 700px. Design contract: design/previews/why/ (1A
 * "understated" variant, closing line in accent treatment).
 */

const BACKEND = import.meta.env.VITE_API_URL ?? ''

const FOCUS_RING =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-accent'

const MEASURE = 'max-w-[700px]'
const RULE = 'border-t border-brand-ink/[0.10]'

export default function WhyPage() {
  // Non-blocking backend warm-up, matching LandingPage — this page exists to
  // move a reader toward signing in. Never gates rendering; failure is silent.
  useEffect(() => {
    fetch(`${BACKEND}/health`).catch(() => {})
  }, [])

  return (
    <div className="min-h-screen w-full bg-brand-surface text-brand-ink font-body antialiased px-[22px] pt-[18px] min-[768px]:px-[44px] min-[768px]:pt-[28px]">
      <div className="mx-auto w-full min-[768px]:max-w-[960px]">
        <PregateHeader />

        <main>
          <section className={`${MEASURE} pt-[36px] pb-[36px] min-[768px]:pt-[56px] min-[768px]:pb-[50px]`}>
            <h1 className="font-serif font-medium text-[31px] min-[768px]:text-[44px] leading-[1.12] tracking-[-0.02em] mb-[14px] min-[768px]:mb-[18px] text-pretty">
              Why I built Proof of Work
            </h1>
            <p className="font-serif text-[17px] min-[768px]:text-[19px] leading-[1.45] text-brand-slate">
              There are two reasons.
            </p>
          </section>

          <section className={`${MEASURE} ${RULE} pt-[30px] pb-[36px] min-[768px]:pt-[42px] min-[768px]:pb-[50px]`}>
            <h2 className="font-serif font-medium text-[22px] min-[768px]:text-[27px] leading-[1.2] tracking-[-0.015em] mb-[14px] min-[768px]:mb-[18px]">
              I needed it.
            </h2>
            <div className="flex flex-col gap-[1em] text-[15px] min-[768px]:text-[16px] leading-[1.65] text-brand-slate">
              <p className="text-pretty">
                I&apos;ve spent a lot of my career building products. I&apos;ve also spent a lot of
                time rebuilding my CV.
              </p>
              <p className="text-pretty">
                Every opportunity asked me to tell the same story a little differently. Healthcare
                emphasizes one part of my experience. Platform work emphasizes another. Product
                leadership another.
              </p>
              <p className="text-pretty">
                And every time, I&apos;d end up digging through old CVs, project documents and
                half-remembered numbers, trying to reconstruct what I actually did.
              </p>
              <p className="text-pretty">I wanted a better source of truth.</p>
            </div>
            <p className="font-serif font-medium text-[18px] min-[768px]:text-[20px] leading-[1.3] tracking-[-0.01em] text-brand-ink mt-[18px] min-[768px]:mt-[20px] pl-[14px] min-[768px]:pl-[16px] border-l-2 border-brand-accent">
              I built it.
            </p>
          </section>

          <section className={`${MEASURE} ${RULE} pt-[30px] pb-[36px] min-[768px]:pt-[42px] min-[768px]:pb-[50px]`}>
            <h2 className="font-serif font-medium text-[22px] min-[768px]:text-[27px] leading-[1.2] tracking-[-0.015em] mb-[14px] min-[768px]:mb-[18px] text-pretty">
              I wanted to build the real thing.
            </h2>
            <div className="flex flex-col gap-[1em] text-[15px] min-[768px]:text-[16px] leading-[1.65] text-brand-slate">
              <p className="text-pretty">
                I&apos;ve spent most of my career working on pieces of software products —
                defining problems, shaping experiences, working with engineering and design,
                getting things shipped. Always a piece of it.
              </p>
              <p className="text-pretty">
                This time, the whole thing. Not a demo, not a proof of concept — a real product
                deployed and running, with persistent data, authentication, access control, AI,
                document generation, deployment and crash logging. All the unglamorous work
                that&apos;s behind software that stays up, keeps your data safe, and works reliably
                as it grows.
              </p>
              <p className="text-pretty">
                I wanted to know whether I could take something all the way to production on my
                own.
              </p>
              <p className="text-pretty">
                AI made the scope possible. The judgment calls — what to build, what to cut, what
                was actually broken — stayed mine.
              </p>
            </div>
            <p className="font-serif font-medium text-[18px] min-[768px]:text-[20px] leading-[1.3] tracking-[-0.01em] text-brand-ink mt-[18px] min-[768px]:mt-[20px] pl-[14px] min-[768px]:pl-[16px] border-l-2 border-brand-accent">
              I built it.
            </p>
          </section>

          <section className={`${MEASURE} ${RULE} pt-[30px] pb-[36px] min-[768px]:pt-[42px] min-[768px]:pb-[50px]`}>
            <h2 className="font-serif font-medium text-[22px] min-[768px]:text-[27px] leading-[1.2] tracking-[-0.015em] mb-[14px] min-[768px]:mb-[18px]">
              Where is it now?
            </h2>
            <div className="flex flex-col gap-[1em] text-[15px] min-[768px]:text-[16px] leading-[1.65] text-brand-slate">
              <p className="text-pretty">
                Proof of Work is still early. I&apos;m deliberately opening it to a small group
                while the core product continues to take shape.
              </p>
              <p className="text-pretty">
                But this isn&apos;t a prototype or a concept page. It&apos;s a deployed product
                that I&apos;m using and continuing to build.
              </p>
            </div>
          </section>

          <section className={`${MEASURE} pt-[48px] pb-[40px] min-[768px]:pt-[76px] min-[768px]:pb-[54px]`}>
            <div className="h-[2px] w-[48px] bg-brand-accent mb-[24px]" aria-hidden="true" />
            <p className="font-serif font-medium text-[24px] min-[768px]:text-[30px] leading-[1.2] tracking-[-0.02em] text-brand-accent text-pretty">
              If you got a CV from me, it was built in Proof of Work.
            </p>
          </section>
        </main>

        <footer className={`${RULE} flex flex-col gap-[9px] pt-[22px] pb-[40px] min-[768px]:pb-[44px] text-[12.5px] leading-[1.6] text-brand-slate`}>
          <span>
            Built by <span className="text-brand-ink">Yosef Sukenik</span>
          </span>
          <span>
            If you want to talk about any of this, I&apos;m on{' '}
            <a
              href="https://linkedin.com/in/sukenik"
              rel="noopener noreferrer"
              className={`text-brand-accent underline underline-offset-2 hover:text-brand-ink ${FOCUS_RING}`}
            >
              LinkedIn
            </a>
            .
          </span>
          <a
            href="/"
            className={`self-start mt-[4px] hover:text-brand-ink hover:underline ${FOCUS_RING}`}
          >
            ← Back to Proof of Work
          </a>
        </footer>
      </div>
    </div>
  )
}
